import React from "react";
import TextField from "@mui/material/TextField";
import IgnoreZoneBuilder from "../../../../common/IgnoreZoneBuilder";
import { IgnoreZone } from "../SiteAboutPageUtils/siteAboutInterfaces";
import { CameraContext, FlexPreset } from "./types";
import { pointsCreator } from "../../../EditIgnoreZones/EditIgnoreZonesUtils/pointsCreatorLarger";

const createEditableZones = (zones: IgnoreZone[]): IgnoreZone[] => {
  if (zones.length > 0) {
    return zones;
  }
  return [
    {
      zone_type: 0,
      stream_id: 0,
      stream_name: "",
      metric_id: 0,
      label: "ignore_motion",
      label_display_name: "Ignore All",
      line_color: "#FF8400",
      coordinates: [],
    },
  ];
};

const transformCoordinates = (
  coords: number[][][],
  adjustedDimensions: { x: number; y: number }
): number[][][] => {
  return coords.map((group) =>
    group.map(([x, y]) => [x * adjustedDimensions.x, y * adjustedDimensions.y])
  );
};

export const PresetEditorStep = ({
  cameraName,
  cameraContext,
  preset,
  onPresetNameChange,
  onPresetZonesChange,
  showPresetMeta = true,
}: {
  cameraName: string;
  cameraContext: CameraContext | undefined;
  preset: FlexPreset;
  onPresetNameChange: (value: string) => void;
  onPresetZonesChange: (zones: IgnoreZone[]) => void;
  showPresetMeta?: boolean;
}) => {
  const [visibleIgnoreZones, setVisibleIgnoreZones] = React.useState<IgnoreZone[]>(
    createEditableZones(preset.zones)
  );
  const [selectedIgnoreZone, setSelectedIgnoreZone] = React.useState(
    createEditableZones(preset.zones)[0]?.label || ""
  );
  const visibleZonesLiveRef = React.useRef<IgnoreZone[]>([]);
  const selectedZoneLiveRef = React.useRef("");
  visibleZonesLiveRef.current = visibleIgnoreZones;
  selectedZoneLiveRef.current = selectedIgnoreZone;
  const [ignorezoneEditorVisible, setIgnorezoneEditorVisible] =
    React.useState(false);
  const [, setEditsMade] = React.useState(false);
  const [largerMode, setLargerMode] = React.useState(false);
  const [iZBuilder, setIZBuilder] = React.useState(<></>);
  const [previewLoadFailed, setPreviewLoadFailed] = React.useState(false);
  const onPresetZonesChangeRef = React.useRef(onPresetZonesChange);
  const lastSyncedZonesRef = React.useRef<string>("");
  const presetNameLocked = ignorezoneEditorVisible;

  React.useEffect(() => {
    const zones = createEditableZones(preset.zones);
    setVisibleIgnoreZones(zones);
    setSelectedIgnoreZone(zones[0]?.label || "");
    setIgnorezoneEditorVisible(false);
    setLargerMode(false);
  }, [preset.localId, preset.zones]);

  React.useEffect(() => {
    onPresetZonesChangeRef.current = onPresetZonesChange;
  }, [onPresetZonesChange]);

  React.useEffect(() => {
    setPreviewLoadFailed(false);
  }, [cameraContext?.preview?.previewUrl, preset.localId]);

  React.useEffect(() => {
    const serializedZones = JSON.stringify(visibleIgnoreZones);
    if (serializedZones === lastSyncedZonesRef.current) {
      return;
    }
    lastSyncedZonesRef.current = serializedZones;
    onPresetZonesChangeRef.current(visibleIgnoreZones);
  }, [visibleIgnoreZones]);

  const handleIgnoreZoneClick = (object: IgnoreZone) => {
    setIgnorezoneEditorVisible(true);
    localStorage.setItem("ignoreZoneColor", object.line_color);
    localStorage.setItem("ignoreZones", JSON.stringify(object.coordinates));
  };

  React.useEffect(() => {
    if (!cameraContext?.preview) {
      return;
    }
    if (ignorezoneEditorVisible) {
      setIZBuilder(
        <IgnoreZoneBuilder
          shapeColor={localStorage.ignoreZoneColor || "#FF8400"}
          height={cameraContext.preview.adjustedDimensions.y}
          width={cameraContext.preview.adjustedDimensions.x}
          imageURL={cameraContext.preview.previewUrl}
          shapesArray={transformCoordinates(
            JSON.parse(localStorage.ignoreZones || "[]"),
            cameraContext.preview.adjustedDimensions
          )}
          setIgnorezoneEditorVisible={setIgnorezoneEditorVisible}
          setEditsMade={setEditsMade}
          visibleIgnoreZones={visibleIgnoreZones}
          selectedIgnoreZone={selectedIgnoreZone}
          setVisibleIgnoreZones={setVisibleIgnoreZones}
          largerMode={largerMode}
          setLargerMode={setLargerMode}
          dimensions={cameraContext.preview.adjustedDimensions}
          setIZBuilder={setIZBuilder}
          allIgnoreZonesShown={false}
          siteSchedules={[]}
          visibleIgnoreZonesLiveRef={visibleZonesLiveRef}
          selectedIgnoreZoneLiveRef={selectedZoneLiveRef}
        />
      );
    } else {
      setIZBuilder(<></>);
    }
  }, [ignorezoneEditorVisible]);

  const previewUrl = (cameraContext?.preview?.previewUrl || "").trim();
  const adjustedDimensions = cameraContext?.preview?.adjustedDimensions;
  const previewUnavailable =
    !cameraContext?.preview || !adjustedDimensions || previewUrl.length === 0 || previewLoadFailed;

  if (previewUnavailable) {
    return (
      <div>
        {showPresetMeta && (
          <>
            <p className="font-mulish text-[18px] font-black pb-[10px]">
              Create Preset for {cameraName}
            </p>
            <TextField
              fullWidth
              label="Preset Name"
              variant="standard"
              sx={{ width: 360 }}
              value={preset.name}
              disabled={presetNameLocked}
              onChange={(event) => {
                if (presetNameLocked) {
                  return;
                }
                onPresetNameChange(event.target.value);
              }}
              inputProps={{ style: { fontFamily: "Mulish" } }}
            />
          </>
        )}
        <p className="font-mulish text-[14px] pt-[12px]">Camera preview unavailable.</p>
      </div>
    );
  }

  return (
    <div>
      {showPresetMeta && (
        <>
          <p className="font-mulish text-[18px] font-black pb-[10px] pl-[25px]">
            Create Preset for {cameraName}
          </p>
          <div className="pt-[12px] pl-[25px]">
            <TextField
              fullWidth
              label="Preset Name"
              variant="standard"
              sx={{ width: 360 }}
              value={preset.name}
              disabled={presetNameLocked}
              onChange={(event) => {
                if (presetNameLocked) {
                  return;
                }
                onPresetNameChange(event.target.value);
              }}
              inputProps={{ style: { fontFamily: "Mulish" } }}
            />
          </div>
        </>
      )}
      <div className="relative min-h-[640px] w-full">
        {!ignorezoneEditorVisible && (
          <div className="flex w-full flex-row items-start gap-6">
            <div className="shrink-0 pl-[20px] pt-[78px]">
              <div
                className="relative"
                style={{
                  width: adjustedDimensions.x,
                  height: adjustedDimensions.y,
                }}
              >
                <img
                  src={previewUrl}
                  onError={() => setPreviewLoadFailed(true)}
                  className="block"
                  style={{
                    width: `${adjustedDimensions.x}px`,
                    height: `${adjustedDimensions.y}px`,
                  }}
                  alt={cameraName}
                />
                {visibleIgnoreZones.map((object) => (
                  <div className="absolute top-[0px] left-[0px]" key={object.label}>
                    {object.coordinates[0] &&
                      object.coordinates.map((coordinates, index) => (
                        <svg
                          className="absolute top-[0px] left-[0px]"
                          key={`${object.label}-${index}`}
                          style={{
                            width: `${adjustedDimensions.x}px`,
                            height: `${adjustedDimensions.y}px`,
                          }}
                        >
                          <polygon
                            points={pointsCreator(
                              coordinates,
                              adjustedDimensions.y,
                              adjustedDimensions.x
                            )}
                            stroke={object.line_color}
                            strokeWidth="5"
                            fill={object.line_color}
                            fillOpacity="50%"
                          />
                        </svg>
                      ))}
                  </div>
                ))}
              </div>
            </div>
            <div className="min-w-0 flex-1 pt-[70px] pl-[40px]">
              <p className="font-mulish text-[18px] font-black text-actuate-blue pb-[14px] pr-2 leading-snug">
                Click the Ignore Zone below you&apos;d like to edit.
              </p>
              <div className="min-h-[360px] w-full overflow-auto pr-2 pt-[20px]">
                <div className="w-full">
                  {visibleIgnoreZones.map((object) => (
                    <div
                      key={object.label}
                      role="button"
                      tabIndex={0}
                      className="relative"
                      onClick={() => {
                        setSelectedIgnoreZone(object.label);
                        handleIgnoreZoneClick(object);
                        setLargerMode(false);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          setSelectedIgnoreZone(object.label);
                          handleIgnoreZoneClick(object);
                          setLargerMode(false);
                        }
                      }}
                    >
                      <div
                        style={{
                          height: 25,
                          width: 6,
                          background: `${object.line_color}`,
                        }}
                        className="absolute"
                      />
                      <p className="font-mulish text-[16px] indent-5">
                        {object.label_display_name}
                      </p>
                      &nbsp;
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div
          className="player absolute top-[25px] left-[17px] h-[200px] w-[775px]"
          role="button"
          tabIndex={0}
        >
          {iZBuilder}
        </div>
      </div>

      {ignorezoneEditorVisible && !largerMode && (
        <div className="relative left-[850px] top-[-555px] w-[570px] max-w-[calc(100vw-120px)] font-mulish text-[15px] text-actuate-blue pr-[20px]">
          <p className="font-bold text-[15px]">INSTRUCTIONS:</p>
          <br />
          <p className="font-normal text-[15px] ">
            To begin creating a shape click anywhere on the image to start adding
            points.
          </p>
          <br />
          <p className="font-normal text-[15px]">
            To finish creating a shape, click on the first point created.
          </p>
          <br />
          <p className="font-normal text-[15px]">
            To modify the shape click and drag the points to the desired location.
          </p>
          <br />
          <p className="font-normal text-[15px]">
            To add additional points to the shape, click on the line between two
            points.
          </p>
          <br />
          <p className="font-normal text-[15px]">
            To create an additional shape, click anywhere on the image to start
            adding points.
          </p>
          <br />
          <p className="font-normal text-[15px]">
            To delete a shape hover anywhere over the shape and click to select. Then
            press the delete key on your keyboard.
          </p>
          <br />
          <p className="font-normal text-[15px]">
            To delete a single point double click on the point. This action will only
            work if the remaining shape will have at least 3 points.
          </p>
        </div>
      )}
    </div>
  );
};

